import React, { ChangeEvent, useEffect, useState } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import {
  Button,
  Form,
  Grid,
  Input,
  Picker,
  RadioGroup,
  Row,
  Select,
  SelectForm
} from 'components'
import SimpleCollapse from 'components/Collapse/SimpleCollapse'
import { useLoading, useModal } from 'hooks'
import { Control, useForm } from 'react-hook-form'
import {
  EfetivoLogicoDto,
  EfetivoLogicoServices,
  EffectiveData
} from 'services/Supplier/LogicalEffective'
import { buscaCep } from 'services/Utils/buscaCep'
import { FORMAT, SPACING, STATES } from 'utils'

import { SCHEMA } from '../../../yup/schema'

interface FormProps {
  handleAddNewColaborator: any
  handleCancel: any
}

interface SelectableData {
  sgGenero: string
  txNacionalidade: string
  sgEstadoDocto: string
  sgEstadoCtps: string
  sgEstadoEnd: string
}

export const DEFAULT_SELECTABLE_DATA: SelectableData = {
  sgEstadoCtps: '',
  sgEstadoDocto: '',
  sgEstadoEnd: '',
  sgGenero: '',
  txNacionalidade: ''
}

export const DadosColaborador: React.FC<FormProps> = ({
  handleAddNewColaborator,
  handleCancel
}) => {
  const [selectableData, setSelectableData] = useState<SelectableData>(DEFAULT_SELECTABLE_DATA)

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    setError,
    clearErrors,
    watch,
    formState: { errors }
  } = useForm<EffectiveData>({
    resolver: yupResolver(SCHEMA.logicalEffectiveForm)
  })

  const form = watch()

  const [effectiveData, setEffectiveData] = useState<
    EfetivoLogicoDto | undefined
  >()

  const { setLoading } = useLoading()
  const { showModal } = useModal()

  const genderValue = watch('sgGenero')
  const txNacionalidade = watch('txNacionalidade')
  const sgEstadoDocto = watch('sgEstadoDocto')
  const sgEstadoCtps = watch('sgEstadoCtps')
  const cpf = watch('cdCpf')
  const sgEstadoEnd = watch('sgEstadoEnd')
  const cdCep = watch('cdCep')
  const orgaoEmissor = watch('txEmissorDocto')
  const endereco = watch('txEndereco')

  const statesList = STATES.map((state) => ({ label: state, value: state }))

  const subtitleCollapse = 'MATRÍCULA:'

  const handleChangeOrgaoEmissor = () => {
    const newValue = form?.txEmissorDocto
      .replace(/[^A-Za-z]/g, '')
      .toLocaleUpperCase()
    setValue('txEmissorDocto', newValue)
  }

  useEffect(() => {
    if (effectiveData) {
      Object.keys(effectiveData).forEach((key) => {
        setValue(key as any, effectiveData[key])
      })

      Object.keys(selectableData).forEach((key) => {
        setSelectableData((oldData) => ({
          ...oldData,
          [key]: effectiveData[key]
        }))
      })
    }
  }, [effectiveData])

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSelectableData((oldData) => ({
      ...oldData,
      [event.target.name]: event.target.value
    }))

    setValue(event.target.name as any, event.target.value)
  }

  useEffect(() => {
    const isNotANumber = isNaN(Number(FORMAT.unMask(cpf)))

    if (
      (isNotANumber && cpf?.length === 13) ||
      (!isNotANumber && cpf?.length === 14)
    ) {
      setLoading('loading')
      const unmaskedCpf = FORMAT.unMask(cpf)

      EfetivoLogicoServices()
        .getEfetivoLogicoByCpf(unmaskedCpf)
        .then(({ data, status }) => {
          if (status === 200) {
            const efetivoLogico = data[0]

            if (efetivoLogico) {
              showModal({
                handleConfirm: () => setEffectiveData(efetivoLogico),
                titleModal: 'Deseja carregar as informações do CPF informado?',
                txtButtonCancel: 'Não',
                txtButtonConfirm: 'Sim'
              })
            }
          }
        })
        .catch(() => {
          showModal({
            hideButtonCancel: true,
            message: 'Não foi possivel obter os dados da pesquisa',
            txtButtonConfirm: 'OK',
            type: 'alert'
          })
          setLoading('idle')
        })
        .finally(() => setLoading('idle'))
    }
  }, [cpf])

  useEffect(() => {
    reset(
      {
        sgEstadoEnd: '',
        txBairro: '',
        txCidade: '',
        txEndereco: ''
      },
      {
        keepValues: true
      }
    )
    setValue('txEndereco', '')
    setValue('txBairro', '')
    setValue('txCidade', '')
    setValue('sgEstadoEnd', '')
    if (cdCep?.length > 7) {
      buscaCep(cdCep).then(({ data }) => {
        setValue('txEndereco', data?.logradouro)
        setValue('txBairro', data?.bairro)
        setValue('txCidade', data?.localidade)

        handleOnChange({
          target: { name: 'sgEstadoEnd', value: data?.uf }
        } as ChangeEvent<HTMLInputElement>)
      })
    }
  }, [cdCep])

  useEffect(() => {
    if (cdCep?.length > 0 && !endereco) {
      setError('cdCep', { message: 'CEP inválido' })
    } else {
      clearErrors('cdCep')
    }
  }, [endereco, cdCep])

  useEffect(() => {
    if (orgaoEmissor) handleChangeOrgaoEmissor()
  }, [orgaoEmissor])

  return (
    <Form
      onSubmit={handleSubmit((data) => {
        handleAddNewColaborator({
          ...selectableData,
          ...data,
        })
      })}
    >
      <>
        <SimpleCollapse
          title="DADOS DO COLABORADOR"
          subtitle={subtitleCollapse}
          isExpanded
        >
          <Grid
            gridCols="2.1fr 2fr 2fr 2fr"
            gridGap="1rem"
            padding={`0 0 ${SPACING.spacing30}`}
          >
            <Input
              control={control}
              errors={errors}
              label="CPF / Passaporte"
              placeholder="Documento do Colaborador"
              formValue="cdCpf"
              maxLength={isNaN(parseInt(form.cdCpf)) ? 13 : 14}
              mask={
                isNaN(parseInt(form.cdCpf))
                  ? ['NN###########']
                  : ['###.###.###-##']
              }
              required
            />
            <Input
              control={control}
              errors={errors}
              label="Colaborador"
              placeholder="Nome do Empregado"
              formValue="nmColaborador"
              maxLength={50}
              required
            />
            <Input
              control={control}
              errors={errors}
              label="Função"
              placeholder="Nome da Função"
              formValue="txFuncao"
              maxLength={50}
              required
            />
            <RadioGroup
              control={control}
              label="Refeitório (Opcional)"
              row
              formValue="usaRefeitorio"
              defaultValue={false}
              listOptions={[
                { label: 'Sim', value: true },
                { label: 'Não', value: false }
              ]}
            />
          </Grid>
          <Grid
            gridCols="2.1fr 2fr 2fr 2fr"
            gridGap="1rem"
            padding={`0 0 ${SPACING.spacing30}`}
          >
            <Input
              control={control}
              errors={errors}
              label="Pai do Colaborador"
              placeholder="Nome do Pai"
              formValue="txFiliacaoPai"
              maxLength={50}
            />
            <Input
              control={control}
              errors={errors}
              label="Mãe do Colaborador"
              placeholder="Nome da Mãe"
              formValue="txFiliacaoMae"
              maxLength={50}
              required
            />
          </Grid>
          <Grid
            gridCols="1fr 1fr 2fr 2fr 2fr"
            gridGap="1rem"
            padding={`0 0 ${SPACING.spacing30}`}
          >
            <Picker
              control={control as Control<any, any>}
              errors={errors}
              defaultValue={null}
              formValue="dtNascimento"
              label="Data de Nascimento"
            />
            <SelectForm
              control={control}
              errors={errors}
              defaultValue={genderValue}
              formValue="sgGenero"
              required
              listOptions={[
                { label: 'Masculino', value: 'M' },
                { label: 'Feminino', value: 'F' }
              ]}
              label="Gênero"
            />
            <Select
              defaultValue={txNacionalidade}
              value={selectableData.txNacionalidade}
              onChange={handleOnChange}
              formValue="txNacionalidade"
              required
              listOptions={[
                { label: 'Brasileiro', value: 'Brasileiro' },
                { label: 'Estrangeiro', value: 'Estrangeiro' }
              ]}
              label="Nacionalidade"
            />
            <Input
              control={control}
              errors={errors}
              label="Naturalidade (Opcional)"
              placeholder="Local de Nascimento"
              formValue="txNaturalidade"
              maxLength={20}
            />
          </Grid>
          <Grid
            gridCols="2.1fr 0.9fr 1fr 2fr 2fr"
            gridGap="1rem"
            padding={`0 0 ${SPACING.spacing30}`}
          >
            <Input
              control={control}
              errors={errors}
              label="RG"
              placeholder="RG do Colaborador"
              formValue="cdDocumento"
              //mask={['##.###.###-##']}
              maxLength={15}
              required
            />
            <Input
              control={control}
              errors={errors}
              label="Órgão"
              placeholder="Órgão Emissor"
              formValue="txEmissorDocto"
              maxLength={7}
              required
            />
            <SelectForm
              control={control}
              errors={errors}
              defaultValue={sgEstadoDocto}
              formValue="sgEstadoDocto"
              listOptions={statesList}
              label="UF"
            />
          </Grid>
          <Grid
            gridCols="2.1fr 0.9fr 1fr 2fr 2fr"
            gridGap="1rem"
            padding={`0 0 ${SPACING.spacing30}`}
          >
            <Input
              control={control}
              errors={errors}
              label="CTPS"
              placeholder="Nº"
              formValue="cdCtps"
              mask={['#############']}
              maxLength={13}
              required
            />
            <Input
              control={control}
              errors={errors}
              label="Série"
              placeholder="Nº"
              formValue="cdSerieCtps"
              mask={['####']}
              maxLength={4}
              required
            />
            <SelectForm
              control={control}
              errors={errors}
              defaultValue={sgEstadoCtps}
              formValue="sgEstadoCtps"
              listOptions={statesList}
              label="UF"
              required
            />
            <Input
              control={control}
              errors={errors}
              label="PIS"
              placeholder="Nº"
              formValue="cdPis"
              mask={['###.#####.##-#']}
              maxLength={14}
              required
            />
            <Input
              control={control}
              errors={errors}
              label="Prontuário (CNH) (Opcional)"
              placeholder="Nº"
              formValue="cdCnh"
              mask={['#########-##']}
              maxLength={12}
            />
          </Grid>
          <Grid
            gridCols="1fr 4fr 1fr 2fr"
            gridGap="1rem"
            padding={`0 0 ${SPACING.spacing30}`}
          >
            <Input
              control={control}
              errors={errors}
              label="CEP"
              formValue="cdCep"
              maxLength={9}
              placeholder="00000-000"
              mask={['#####-###']}
              required
            />
            <Input
              control={control}
              errors={errors}
              label="Endereço"
              placeholder="Logradouro"
              formValue="txEndereco"
              maxLength={50}
              required
            />
            <Input
              control={control}
              errors={errors}
              label="Número (Opcional)"
              placeholder="Nº do Logradouro"
              formValue="nuNumero"
              maxLength={4}
            />
            <Input
              control={control}
              errors={errors}
              label="Complemento"
              placeholder="Ex. Casa/Fundos"
              formValue="txComplemento"
              maxLength={50}
            />
          </Grid>
          <Grid
            gridCols="2.5fr 2.5fr 1fr 2fr"
            gridGap="1rem"
            padding={`0 0 ${SPACING.spacing30}`}
          >
            <Input
              control={control}
              errors={errors}
              label="Bairro"
              placeholder="Bairro"
              formValue="txBairro"
              maxLength={40}
              required
            />
            <Input
              control={control}
              errors={errors}
              label="Cidade"
              placeholder="Cidade"
              formValue="txCidade"
              maxLength={50}
              required
            />
            <Select
              defaultValue={sgEstadoEnd}
              value={selectableData.sgEstadoEnd}
              onChange={handleOnChange}
              formValue="sgEstadoEnd"
              listOptions={statesList}
              label="UF"
              required
            />
            <Input
              control={control}
              errors={errors}
              label="Local de Acesso (Opcional)"
              placeholder="Local de Acesso"
              formValue="txLocalAcesso"
              maxLength={50}
            />
          </Grid>
        </SimpleCollapse>
        <Row
          justifyContent="flex-end"
          marginTop={SPACING.spacing30}
          marginBottom={SPACING.spacing20}
        >
          <Button
            text="Cancelar"
            variantButton="outlined"
            onClick={() => {
              reset()
              handleCancel()
            }}
            marginRight={SPACING.spacing20}
          />
          <Button
            text="Salvar"
            type="submit"
          />
        </Row>
      </>
    </Form>
  )
}
